// Demo view's usage by person: sample people and sample conversation inventories in the exact client-safe
// shapes the Alloy people route and the conversation routes return. Dashboard conversation ids follow the real
// grammar (`cdash2--<ownerKey>--<stamp>--<random>`), so the page groups them the way it groups live data.

import { DEMO_CLIENT_SLUG } from "@/demo/demoData";

const DAY_MS = 24 * 60 * 60 * 1000;
const ISSUER_NOTE = "demo"; // keys below are fixed sample strings, not hashes of anyone
// One clock for the whole demo session: the provider page and the history page must mint the same
// conversation ids, or the page would see every conversation twice.
const DEMO_NOW = Date.now();

const PEOPLE = [
  { ownerKey: "a1b2c3d4e5f60718293a4b5c6d7e8f90", email: "maya.chen@demo-workspace.com", role: "owner", status: "active", you: true, seed: 11, chats: 28 },
  { ownerKey: "b2c3d4e5f60718293a4b5c6d7e8f90a1", email: "daniel.okafor@demo-workspace.com", role: "admin", status: "active", you: false, seed: 23, chats: 21 },
  { ownerKey: "c3d4e5f60718293a4b5c6d7e8f90a1b2", email: "priya.raman@demo-workspace.com", role: "member", status: "active", you: false, seed: 37, chats: 16 },
  { ownerKey: "d4e5f60718293a4b5c6d7e8f90a1b2c3", email: "tom.hale@demo-workspace.com", role: "member", status: "active", you: false, seed: 41, chats: 9 },
  { ownerKey: "e5f60718293a4b5c6d7e8f90a1b2c3d4", email: "leah.fischer@demo-workspace.com", role: "member", status: "revoked", you: false, seed: 53, chats: 4 },
  { ownerKey: "f60718293a4b5c6d7e8f90a1b2c3d4e5", email: "jonas.weber@demo-workspace.com", role: "member", status: "active", you: false, seed: 67, chats: 0 },
];

const TITLES = [
  "Q4 launch email sequence, three drafts", "Meta ads: which creative to cut this week", "Rewrite the returns policy page",
  "Weekly growth brief questions", "Competitor pricing scan", "Shopify discount code rules", "Klaviyo flow audit",
  "TikTok ads report for Monday", "Landing page copy test, variant B", "Which SKUs to feature this month",
  "Customer review themes, last 60 days", "Influencer shortlist for the winter drop", "UGC brief for creators",
  "Help desk macros for shipping delays", "Sort last week's support tickets by theme", "Brand voice check on the newsletter",
  "Alt text for the new product photos", "Connect Google Analytics from the dashboard", "Prep the cozy weekend emails",
];

// mulberry32: the same small deterministic generator the skyline uses, so the sample never changes between loads.
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const hex = (random, length) => Array.from({ length }, () => Math.floor(random() * 16).toString(16)).join("");

function conversationsFor(person, now) {
  const random = rng(person.seed);
  const rows = [];
  for (let index = 0; index < person.chats; index += 1) {
    const daysAgo = Math.floor(Math.pow(random(), 1.6) * 330);
    const startedAt = new Date(now - daysAgo * DAY_MS - Math.floor(random() * 10) * 60 * 60 * 1000);
    const requests = 2 + Math.floor(random() * 18);
    const input = requests * (1800 + Math.floor(random() * 5200));
    const output = Math.floor(input * (0.18 + random() * 0.2));
    const cached = Math.floor(input * random() * 0.45);
    const recorded = random() > 0.12;
    rows.push({
      id: `cdash2--${person.ownerKey}--${startedAt.getTime().toString(36)}--${hex(random, 24)}`,
      title: TITLES[Math.floor(random() * TITLES.length)],
      startedAt: startedAt.toISOString(),
      updatedAt: new Date(startedAt.getTime() + Math.floor(random() * 50) * 60 * 1000).toISOString(),
      requestCount: requests,
      inputTokens: input,
      outputTokens: output,
      cachedTokens: cached,
      totalTokens: input + output,
      actualCostUsd: recorded ? Math.round((input * 0.0000018 + output * 0.0000072) * 10000) / 10000 : null,
      messageCount: requests * 2,
    });
  }
  return rows;
}

function groupRows(now) {
  const random = rng(101);
  const rows = [];
  for (let index = 0; index < 12; index += 1) {
    const daysAgo = Math.floor(random() * 300);
    const startedAt = new Date(now - daysAgo * DAY_MS);
    const requests = 20 + Math.floor(random() * 300);
    const input = requests * (9000 + Math.floor(random() * 30000));
    const output = Math.floor(input * 0.12);
    const slack = index % 3 !== 0;
    rows.push({
      id: slack ? `slack-${hex(random, 12)}` : `cron-${hex(random, 10)}`,
      title: slack ? ["Update announcement bar link", "Prep cozy fall weekend emails", "Pull last week's ad numbers"][index % 3] : ["Daily growth brief", "Daily blog draft builder"][index % 2],
      source: slack ? "slack" : "cron",
      startedAt: startedAt.toISOString(),
      updatedAt: startedAt.toISOString(),
      requestCount: slack ? requests : null,
      inputTokens: slack ? input : null,
      outputTokens: slack ? output : null,
      cachedTokens: slack ? Math.floor(input * 0.3) : null,
      totalTokens: slack ? input + output : null,
      actualCostUsd: slack ? Math.round((input * 0.0000018 + output * 0.0000072) * 100) / 100 : null,
      messageCount: slack ? requests : 3,
    });
  }
  return rows;
}

function allRows(now) {
  return [...PEOPLE.flatMap((person) => conversationsFor(person, now)), ...groupRows(now)]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

// Sample Slack people: the first Slack conversations started by Maya (a member, matched by email on the server),
// the next by someone in Slack who is not on the dashboard team; the rest stay unnamed under Slack.
const DEMO_SLACK_PEOPLE = [
  { slackUserId: "U0DEMOMAYA", name: "Maya Chen", ownerKey: PEOPLE[0].ownerKey },
  { slackUserId: "U0DEMOSAM", name: "Sam Rivera", ownerKey: null },
];

export function buildDemoAlloySlackPeople(now = DEMO_NOW) {
  const slackIds = groupRows(now).filter((row) => row.source === "slack").map((row) => row.id);
  const sessions = {};
  slackIds.forEach((id, index) => {
    if (index % 3 === 2) return;
    sessions[id] = DEMO_SLACK_PEOPLE[index % 3].slackUserId;
  });
  return { clientSlug: DEMO_CLIENT_SLUG, available: true, people: DEMO_SLACK_PEOPLE, sessions, complete: true };
}

export function buildDemoAlloyPeople() {
  return {
    clientSlug: DEMO_CLIENT_SLUG,
    people: PEOPLE.map(({ ownerKey, email, role, status, you }) => ({ ownerKey, email, name: null, role, status, you })),
  };
}

/** The provider page: every conversation's recorded usage, as one complete chunk. */
export function buildDemoConversationPage(now = DEMO_NOW) {
  const rows = allRows(now);
  const since = new Date(now - 340 * DAY_MS).toISOString();
  const asOf = new Date(now).toISOString();
  return {
    clientSlug: DEMO_CLIENT_SLUG,
    sessions: rows.filter((row) => row.requestCount != null).map((row) => ({
      id: row.id,
      requestCount: row.requestCount,
      inputTokens: row.inputTokens,
      outputTokens: row.outputTokens,
      cachedTokens: row.cachedTokens,
      totalTokens: row.totalTokens,
      actualCostUsd: row.actualCostUsd,
    })),
    unassigned: { id: null, requestCount: 14, inputTokens: 41200, outputTokens: 8300, cachedTokens: 0, totalTokens: 49500, actualCostUsd: 0.21 },
    nextCursor: null,
    coverage: {
      scope: "provider-retained-current-connection",
      since,
      asOf,
      chunkStart: new Date(now - 31 * DAY_MS).toISOString(),
      chunkEnd: asOf,
      complete: true,
      errors: [],
      actualCostBasis: `Charged Alloy tokens; sample data (${ISSUER_NOTE})`,
    },
  };
}

/** The agent's history page: titles, dates and sources for the same conversations. */
export function buildDemoConversationHistory(now = DEMO_NOW) {
  return {
    clientSlug: DEMO_CLIENT_SLUG,
    available: true,
    sessions: allRows(now).map((row) => ({
      id: row.id,
      title: row.title,
      source: row.source === "slack" ? "Slack" : row.source === "cron" ? "Automations" : "Dashboard",
      startedAt: row.startedAt,
      updatedAt: row.updatedAt,
      messageCount: row.messageCount,
      parentSessionId: null,
      archived: false,
      actualCostUsd: null,
    })),
    nextOffset: null,
    error: null,
    coverage: { pagination: "supported", archives: "not-enumerated", children: "included", lineage: "explicit-only" },
  };
}
